// Enregistrement d'une préaffectation sans jamais écraser une modification
// faite ailleurs (plan 070b, décision PO Q19 du 05/10/2026 : « on ne peut pas
// forcer la mise à jour »).
//
// CleanyApp (via careDataParser) modifie la même ligne `care.preaffectation`
// pendant qu'une fiche est ouverte ici : amendements, miroir des décisions.
// Le Zap et Bubble l'écrivent aussi. Le provider générique
// (ra-data-postgrest.js) envoie l'enregistrement entier en PATCH : une
// sauvegarde faite depuis une fiche ouverte avant une modification la
// réécrirait en silence avec les anciennes valeurs.
//
// Au moment d'enregistrer :
// 1. relecture de la ligne ; si une colonne métier diffère de la version
//    ouverte (`previousData`), rien n'est écrit ;
// 2. PATCH des seuls champs modifiés, conditionné par les colonnes
//    structurées de la version ouverte, avec `Prefer: return=representation` ;
//    aucune ligne renvoyée = la ligne a changé entre la relecture et
//    l'écriture, ou care a vidé le corps (rôle sans droit d'édition) ;
// 3. dans ce cas, nouvelle relecture pour dire « modifiée », « supprimée » ou
//    « rien n'a été enregistré ».
//
// `commentaire`, `commentaire_validateur` et `absence_type` ne vont jamais
// dans l'URL : textes libres et motif parfois médical, or le routeur Heroku
// journalise le chemin complet des requêtes. Ils sont couverts par la
// relecture seule. Risque accepté (arbitrage du 05/10) : une modification de
// ces seuls champs pendant l'aller-retour entre la relecture et l'écriture.
//
// Aucun import de react-admin : `fetchJson` est injecté (fetchUtils.fetchJson,
// cf. src/dataProvider.js). Les erreurs ont la forme de HttpError (message,
// status, body).
import { stringify } from 'query-string';

const API_URL = 'api';
const RESOURCE = 'preaffectation';

// Les 23 colonnes métier de care.preaffectation (24 avec `id`), relevées en
// base le 05/10/2026, dans l'ordre des libellés affichés à l'utilisateur. Une
// colonne ajoutée en base n'est comparée qu'une fois inscrite ici, et n'est
// jamais écrite tant que l'utilisateur ne la modifie pas.
export const BUSINESS_FIELDS = [
  'type',
  'cleaner_id',
  'account_id',
  'manager_id',
  'absent_id',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
  'start_date',
  'end_date',
  'start_time',
  'end_time',
  'absence_type',
  'prestation_type',
  'complement_heure',
  'traite',
  'date_creation',
  'commentaire',
  'commentaire_validateur',
];

export const FIELDS_KEPT_OUT_OF_URL = [
  'commentaire',
  'commentaire_validateur',
  'absence_type',
];

export const URL_CONDITION_FIELDS = BUSINESS_FIELDS.filter((field) => {
  return !FIELDS_KEPT_OUT_OF_URL.includes(field);
});

const DAYS = 'jours';

export const FIELD_LABELS = {
  type: 'raison',
  cleaner_id: 'cleaner',
  account_id: 'compte',
  manager_id: 'manager',
  absent_id: 'absent',
  monday: DAYS,
  tuesday: DAYS,
  wednesday: DAYS,
  thursday: DAYS,
  friday: DAYS,
  saturday: DAYS,
  sunday: DAYS,
  start_date: 'dates',
  end_date: 'dates',
  start_time: 'horaires',
  end_time: 'horaires',
  absence_type: "type d'absence",
  prestation_type: 'type de prestation',
  complement_heure: "complément d'heures",
  traite: 'traité',
  date_creation: 'date de création',
  commentaire: 'commentaire manager',
  commentaire_validateur: 'commentaire RH',
};

export const SAVE_ERROR_CODES = {
  conflict: 'PREAFFECTATION_CONFLICT',
  deleted: 'PREAFFECTATION_DELETED',
  notApplied: 'PREAFFECTATION_NOT_APPLIED',
  missingBaseline: 'PREAFFECTATION_BASELINE_MISSING',
};

const hasOwn = (object, key) => {
  return Object.prototype.hasOwnProperty.call(object, key);
};

export const describeFields = (fields) => {
  return fields
    .map((field) => {
      return FIELD_LABELS[field] || field;
    })
    .filter((label, index, labels) => {
      return labels.indexOf(label) === index;
    })
    .join(', ');
};

const messageFor = (code, fields) => {
  switch (code) {
    case SAVE_ERROR_CODES.conflict:
      return `Cette demande a été modifiée depuis l'ouverture de la fiche (${describeFields(
        fields,
      )}). Rien n'a été enregistré. Rechargez la fiche avant d'enregistrer.`;
    case SAVE_ERROR_CODES.deleted:
      return "Cette demande a été supprimée depuis l'ouverture de la fiche. Rien n'a été enregistré.";
    case SAVE_ERROR_CODES.notApplied:
      return "Rien n'a été enregistré : care n'a modifié aucune ligne, alors que la demande n'a pas changé depuis l'ouverture de la fiche. Le rôle Manager ne peut pas modifier une préaffectation au backoffice ; dans les autres cas, signalez-le à l'équipe technique.";
    default:
      return "Rien n'a été enregistré : la version ouverte de la fiche est inconnue. Rechargez la fiche avant d'enregistrer.";
  }
};

// Même forme que HttpError de react-admin : le contrôleur d'édition affiche
// `message`, et l'authProvider ne déconnecte que sur un 401.
export const createSaveError = (code, fields = []) => {
  const error = new Error(messageFor(code, fields));
  error.name = 'PreaffectationSaveError';
  error.status = 409;
  error.body = { code, fields, labels: describeFields(fields) };
  return error;
};

export const isPreaffectationSaveError = (error) => {
  return !!(
    error &&
    error.body &&
    Object.values(SAVE_ERROR_CODES).includes(error.body.code)
  );
};

// Valeur de filtre PostgREST pour une valeur telle que PostgREST l'a renvoyée.
// NULL et booléens passent par `is.` : `eq.null` comparerait à la chaîne
// « null » et échoue sur les colonnes date, time et boolean. Le reste passe
// par `eq.` avec la valeur reprise telle quelle (`query-string` l'encode) :
// chaîne vide comprise (`prestation_type`), heure avec secondes (`08:00:00`).
export const toFilterValue = (value) => {
  if (value === null) {
    return 'is.null';
  }
  if (value === true) {
    return 'is.true';
  }
  if (value === false) {
    return 'is.false';
  }
  return `eq.${value}`;
};

const isFilterable = (value) => {
  return (
    value === null || ['string', 'number', 'boolean'].includes(typeof value)
  );
};

// Filtres de l'écriture conditionnelle : `id`, plus chaque colonne structurée
// chargée à l'ouverture. Une colonne absente de la version ouverte n'est ni
// comparée ni écrite.
export const buildConditionQuery = (id, baseline) => {
  const query = { id: `eq.${id}` };
  URL_CONDITION_FIELDS.forEach((field) => {
    if (!hasOwn(baseline, field) || !isFilterable(baseline[field])) {
      return;
    }
    query[field] = toFilterValue(baseline[field]);
  });
  return query;
};

// Comparaison de valeurs lues en base : stricte (`''` et NULL diffèrent).
const sameStoredValue = (left, right) => {
  const a = left === undefined ? null : left;
  const b = right === undefined ? null : right;
  if (a === b) {
    return true;
  }
  if (a !== null && b !== null && typeof a === 'object') {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return false;
};

// Colonnes métier dont la valeur diffère entre la version ouverte et une
// relecture.
export const changedFields = (baseline, current) => {
  return BUSINESS_FIELDS.filter((field) => {
    return (
      hasOwn(baseline, field) &&
      !sameStoredValue(baseline[field], current[field])
    );
  });
};

// Comparaison formulaire / version ouverte : un champ vidé (`''`, absent)
// vaut NULL, pour ne pas écrire une colonne que l'utilisateur n'a pas changée.
const sameFormValue = (left, right) => {
  const normalize = (value) => {
    return value === undefined || value === '' ? null : value;
  };
  return sameStoredValue(normalize(left), normalize(right));
};

// Corps du PATCH : les seuls champs du formulaire qui diffèrent de la version
// ouverte, jamais `id`.
export const buildPatchBody = (data, baseline) => {
  const body = {};
  Object.keys(data || {}).forEach((key) => {
    if (key === 'id' || sameFormValue(data[key], baseline[key])) {
      return;
    }
    body[key] = data[key];
  });
  return body;
};

const readRow = async (id, fetchJson) => {
  const { json } = await fetchJson(
    `${API_URL}/${RESOURCE}?${stringify({ id: `eq.${id}` })}`,
    { method: 'GET', credentials: 'same-origin' },
  );
  if (!Array.isArray(json)) {
    const error = new Error(
      'Réponse inattendue de care à la relecture de la préaffectation.',
    );
    error.status = 502;
    error.body = json === undefined ? null : json;
    throw error;
  }
  return json.length ? json[0] : null;
};

/**
 * UPDATE d'une préaffectation (appelé par src/dataProvider.js).
 *
 * @param {Object} params `{ id, data, previousData }` fournis par react-admin :
 *   `data` = valeurs du formulaire, `previousData` = enregistrement chargé à
 *   l'ouverture de la fiche.
 * @param {Object} deps `{ fetchJson }` : fetchUtils.fetchJson de react-admin.
 * @returns {Promise<{ data: Object }>} la ligne enregistrée ; rejet avec une
 *   erreur `PreaffectationSaveError` (status 409, `body.code` dans
 *   SAVE_ERROR_CODES) quand rien n'a été écrit.
 */
export const updatePreaffectation = async (params, { fetchJson }) => {
  const { id, data, previousData } = params || {};
  if (!previousData || typeof previousData !== 'object') {
    throw createSaveError(SAVE_ERROR_CODES.missingBaseline);
  }

  // 1. Relecture : toute différence avec la version ouverte bloque l'écriture.
  const current = await readRow(id, fetchJson);
  if (!current) {
    throw createSaveError(SAVE_ERROR_CODES.deleted);
  }
  const changedSinceOpening = changedFields(previousData, current);
  if (changedSinceOpening.length) {
    throw createSaveError(SAVE_ERROR_CODES.conflict, changedSinceOpening);
  }

  // 2. Seuls les champs modifiés partent ; s'il n'y en a aucun, rien à écrire.
  const body = buildPatchBody(data, previousData);
  if (!Object.keys(body).length) {
    return { data: current };
  }

  // 3. Écriture conditionnée par la version ouverte.
  const headers = new Headers();
  headers.set('Prefer', 'return=representation');
  const { json } = await fetchJson(
    `${API_URL}/${RESOURCE}?${stringify(
      buildConditionQuery(id, previousData),
    )}`,
    {
      method: 'PATCH',
      headers,
      credentials: 'same-origin',
      body: JSON.stringify(body),
    },
  );
  if (Array.isArray(json) && json.length) {
    return { data: { ...previousData, ...body, ...json[0] } };
  }

  // 4. Aucune ligne renvoyée : une nouvelle relecture dit pourquoi.
  const after = await readRow(id, fetchJson);
  if (!after) {
    throw createSaveError(SAVE_ERROR_CODES.deleted);
  }
  if (
    !Array.isArray(json) &&
    !changedFields({ ...previousData, ...body }, after).length
  ) {
    // Réponse sans représentation (en-tête Prefer perdu en route) mais ligne
    // conforme à l'écriture demandée : elle a été appliquée.
    return { data: after };
  }
  const changedMeanwhile = changedFields(previousData, after);
  if (changedMeanwhile.length) {
    throw createSaveError(SAVE_ERROR_CODES.conflict, changedMeanwhile);
  }
  throw createSaveError(SAVE_ERROR_CODES.notApplied);
};
